<script setup>
  import { ref, computed, onMounted, watchEffect, watch, onUnmounted } from 'vue';
  import { useProForecastStore } from '@/stores/ProForecastStore';
  import { useUserStore } from '@/stores/UserStore'
  import useLegacyBridge from "@/composables/useLegacyBridge";

  const content = defineModel('content');
  const selectedModelIds = defineModel('selectedModels');

  const props = defineProps({
    isNCWind: {
      type: Boolean,
      required: true
    },
    ncWindDataEnabled: {
      type: Boolean,
      required: false
    },
     useExternalDataBlock: {
      type: Boolean,
      default: false
    },
    zoom: {
      type: Number,
      required: false,
      default: 12
    },
    modelSelectorTarget: {
      type: HTMLElement,
      required: false
    },
    legendTarget: {
      type: HTMLElement,
      required: false
    },
    spotDataObject: {
      type: Object,
      required: true
    },
    useUniqueStoreKey: {
      type: Boolean,
      default: false
    },
    updateKey: {
      type: Number,
      default: () => Date.now()
    },
    isNowcast: {
      type: Boolean,
      default: false
    }
  });

  const uniqueStoreKey = props.useUniqueStoreKey ? Date.now() : null;
  const proForecastStore = useProForecastStore(uniqueStoreKey);
  const userStore = useUserStore();

  const { basicGraph } = useLegacyBridge();

  const uniqueGraphId = Date.now();
  
  const graphContainer = ref(null);
  const modelSelectorContainer = ref(null);
  const ncWindDataEnabled = ref(props.ncWindDataEnabled || false);
  const loaded = ref(false);

  const showProForecastModel = computed(() => {
    return props.spotDataObject.proForecastIdIfExists && props.spotDataObject.spotProducts.includes(3) && proForecastStore.canShowProForecast});
  const proForecastModelId = computed(() => props.spotDataObject.proForecastIdIfExists);

  const getProForecastModel = async () => {
    let spotForecastModelData = null;

    if (userStore.memberLevel >= 49) {
      await proForecastStore.getProForecast(proForecastModelId.value);
      spotForecastModelData = proForecastStore.buildSpotForecastModelData(props.spotDataObject.spotId, props.spotDataObject.localUTCOffset);
    }

    if (!spotForecastModelData) {
      console.warn("No Pro Forecast Model Data available");
      return spotForecastModelData;
    }

    return {
      is_premium: true,
      is_pro_only: true,
      model_name: 'Pro Forecast',
      model_id: -9999,
      model_color: '513A85',
      model_data: spotForecastModelData,
      is_upgrade_available: userStore.memberLevel < 49,
    }
  };
  
  const loadGraph = async () => {
    const settings = {
      graphId: uniqueGraphId,
      showObserved: !props.isNowcast,
      showModelData: true,
      showModelSelector: true,
      modelData: {...props.spotDataObject.forecastModelData},
      modelSelectorTarget: props.modelSelectorTarget ? props.modelSelectorTarget : modelSelectorContainer.value,
      legendTarget: props.legendTarget,
      spotId: props.spotDataObject.spotId,
      favSpotId: props.spotDataObject.spotFavoriteId,
      lat: props.spotDataObject.lat,
      lon: props.spotDataObject.lon,
      type: 'detail',
      zoom: props.zoom,
      isNCWind: props.isNCWind,
			ncWindDataEnabled: ncWindDataEnabled.value
    }
    console.log("🚀 ~ WindGraphBasic.vue ~ loadGraph ~ settings:", JSON.parse(JSON.stringify(settings)))

    console.log("🚀 ~ WindGraphBasic.vue ~ loadGraph ~ selectedModelIds.value:", selectedModelIds.value)
    if (selectedModelIds.value) {
      settings.selectedModelIds = selectedModelIds.value
    }

    if (showProForecastModel.value) {
      const proForecastModelData = await getProForecastModel();

      if (proForecastModelData) settings.additionalModels = [proForecastModelData];
    }
    
    content.value = basicGraph(graphContainer.value, settings);
    loaded.value = true;
  }

  watchEffect(() => ncWindDataEnabled.value = props.ncWindDataEnabled || false);

  onMounted(() => {   
    loadGraph();

    window.$EventBus.addEventListener('jw-wind-graph-models-changed', (e) => {
      const { graphId, selectedModels } = e.detail;

      // The uniqueGraphId/graphId are needed to prevent conflicts on the favorites page.
      if (graphId !== uniqueGraphId) return;

      selectedModelIds.value = selectedModels;
    })
  })  

  onUnmounted(() => {
    if (!props.legendTarget) return;

    const legend = props.legendTarget;
    legend.innerHTML = '';
  });

  watch(() => props.updateKey, () => {
    content.value.refresh();
  })  
</script>

<template>
  <div
    class="jwx-data-container__main-content"
    style="width: 100%; height: 100%; background-color: #ffffff;"
    ref="graphContainer"
  ></div>
 
</template>